import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Linkedin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-[#2D2D2D] text-white">
      {/* Main Footer */}
      <div className="container-main py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <span className="font-heading text-2xl font-semibold">
                Decors <span className="text-[#C5A065]">Genius</span>
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Thoughtful home decoration services designed to transform your living spaces 
              into elegant, personalized sanctuaries.
            </p>
            <div className="flex gap-4">
              <a
                href="#"
                data-testid="social-instagram"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C5A065] transition-colors"
              >
                <Instagram size={18} />
              </a>
              <a
                href="#"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C5A065] transition-colors"
              >
                <Facebook size={18} />
              </a>
              <a
                href="#"
                data-testid="social-linkedin"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C5A065] transition-colors"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'Services', path: '/services' },
                { name: 'Projects', path: '/projects' },
                { name: 'About Us', path: '/about' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-gray-400 hover:text-[#C5A065] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg mb-6">Our Services</h4>
            <ul className="space-y-3">
              {[
                'Living Room Decoration',
                'Accent Wall Styling',
                'Bedroom Enhancements',
                'Lighting Consultation',
                'Seasonal Updates',
              ].map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-gray-400 hover:text-[#C5A065] transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#C5A065] mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  New York, USA
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-[#C5A065] mt-0.5 flex-shrink-0" />
                <a
                  href="tel:+18888801455"
                  data-testid="footer-phone"
                  className="text-gray-400 hover:text-[#C5A065] transition-colors text-sm"
                >
                  +1-888-880-1455
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-[#C5A065] mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:support@decorsgenius.store"
                  data-testid="footer-email"
                  className="text-gray-400 hover:text-[#C5A065] transition-colors text-sm"
                >
                  support@decorsgenius.store
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-[#C5A065] mt-0.5 flex-shrink-0" />
                <div className="text-gray-400 text-sm">
                  <p>Mon - Sat: 9:00 AM - 6:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal Links */}
      <div className="border-t border-white/10">
        <div className="container-main py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-xs text-center md:text-left">
              © {currentYear} Decors Genius. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <Link
                to="/privacy-policy"
                data-testid="footer-privacy"
                className="text-gray-500 hover:text-[#C5A065] transition-colors text-xs"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-conditions"
                data-testid="footer-terms"
                className="text-gray-500 hover:text-[#C5A065] transition-colors text-xs"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/refund-policy"
                data-testid="footer-refund"
                className="text-gray-500 hover:text-[#C5A065] transition-colors text-xs"
              >
                Refund Policy
              </Link>
              <Link
                to="/disclaimer"
                data-testid="footer-disclaimer"
                className="text-gray-500 hover:text-[#C5A065] transition-colors text-xs"
              >
                Disclaimer
              </Link>
              <Link
                to="/cookie-policy"
                data-testid="footer-cookies"
                className="text-gray-500 hover:text-[#C5A065] transition-colors text-xs"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-[#1a1a1a] py-4">
        <div className="container-main">
          <p className="text-gray-600 text-xs text-center">
            Pricing varies based on project scope and materials selected. All estimates 
            are provided in writing before project commencement.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
